import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { inject, observer } from "mobx-react";
import { CalendarDays, Factory, ListTodo, Mail, ReceiptText } from "lucide-react";
import React from "react";
import { FormattedNumber } from "react-intl";
import { PurchaseOrder } from "../../stores/document/Document";
import { RootStore } from "../../stores/rootStore";
import { Card } from "randmarcomps";
import CommentsView from "../comments/CommentsView";
import ReceiptsGrid from '../grids/ReceiptsGrid';
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";
import PurchaseOrderPartNumberRow from "./PurchaseOrderPartNumberRow";
import EditExpectedDate from "./actions/EditExpectedDate";

interface IProps {
  store?: RootStore;
  purchaseOrder: PurchaseOrder;
}

const PurchaseOrderView: React.FC<IProps> = ({ purchaseOrder, store }) => {
  const { warehousesStore } = store!;

  return (
    <>
        <div className="mb-4 flex w-full flex-col gap-4 md:flex-row md:justify-between">
          <Card className="w-full">
            <div className="p-4">
              <h4 style={{ marginBottom: 5, margin: "20px", marginTop: "10px" }}>Purchase Order Information</h4>
              <div className="rounded-md border-l-4 border-blue-400 bg-blue-50 p-4 text-blue-800" role="alert">
                <p className="font-bold">Purchase Order Editing</p>
                <p>You can modify the PO through the actions button at the top right.</p>
              </div>
              <div className="mt-4 flex flex-col items-center justify-end gap-2 md:flex-row">
                <div className="w-full">
                  <div className="flex items-center p-2 hover:bg-violet-50 rounded-lg">
                    <CalendarDays className="mr-3 h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Purchase Order Date</p>
                      <p className="text-sm text-muted-foreground">{FormatDateFromInt(purchaseOrder.purchaseOrderDate)}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-2 hover:bg-violet-50 rounded-lg">
                    <div className="flex items-center">
                      <CalendarDays className="mr-3 h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Expected Arrival Date</p>
                        <p className="text-sm text-muted-foreground">{FormatDateFromInt(purchaseOrder.expectedArrivalDate)}</p>
                      </div>
                    </div>
                    <EditExpectedDate expectedArrivalDate={purchaseOrder.expectedArrivalDate || 0} purchaseOrderNumber={purchaseOrder.purchaseOrderNumber || ""} />
                  </div>
                  <div className="flex items-center p-2 hover:bg-violet-50 rounded-lg">
                    <Factory className="mr-3 h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Location</p>
                      <p className="text-sm text-muted-foreground">{warehousesStore.getWarehouseNameFromCode(purchaseOrder.location)}</p>
                    </div>
                  </div>
                </div>
                <div className="w-full">
                  <div className="flex items-center p-2 hover:bg-violet-50 rounded-lg">
                    <ReceiptText className="mr-3 h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Sub-Total</p>
                      <p className="text-sm text-muted-foreground"><FormattedNumber {...CurrencyUtils.format} value={purchaseOrder.subTotal || 0} /></p>
                    </div>
                  </div>
                  <div className="flex items-center p-2 hover:bg-violet-50 rounded-lg">
                    <ListTodo className="mr-3 h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Terms</p>
                      <p className="text-sm text-muted-foreground">{purchaseOrder.termsCode}</p>
                    </div>
                  </div>
                  <div className="flex items-center p-2 hover:bg-violet-50 rounded-lg">
                    <Mail className="mr-3 h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Contact Email</p>
                      <p className="text-sm text-muted-foreground">{purchaseOrder.contactEmail === "" ? (<span className="italic">Undefined</span>) : purchaseOrder.contactEmail}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          <Card className="w-full md:w-1/2" >
            <div className="p-4">
              <h4 style={{ marginBottom: 5, margin: "20px", marginTop: "10px" }}>History</h4>
              <CommentsView comments={purchaseOrder.comments || ""} />
            </div>
          </Card>
        </div>
        {
          purchaseOrder.receipts && purchaseOrder.receipts!.length !== 0 &&
          <Card className="w-full mb-4">
            <div className="p-4">
              <h4 style={{ marginBottom: 5, margin: "20px", marginTop: "10px" }}>Receptions ({purchaseOrder.receipts.length})</h4>
              <ReceiptsGrid receipts={purchaseOrder.receipts} />
            </div>
          </Card>
        }

        <Card className="w-full mb-4">
          <div className="p-4">
            <h4 style={{ marginBottom: 5, margin: "20px", marginTop: "10px" }}>Awaiting Part Numbers ({(purchaseOrder.partNumbers && purchaseOrder.partNumbers!)?.length})</h4>
            <div style={{ borderRadius: '20px', width: '100%', overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f9f5ff" }}>
                    <TableCell align="center" sx={{ borderColor: "#582c83" }}>Product</TableCell>
                    <TableCell align="center" sx={{ borderColor: "#582c83" }}>Quantity</TableCell>
                    <TableCell sx={{ borderColor: "#582c83" }} />
                    <TableCell align="center" sx={{ borderColor: "#582c83" }}>Unit Price</TableCell>
                    <TableCell align="center" sx={{ borderColor: "#582c83" }}>Extended Price</TableCell>
                    <TableCell sx={{ borderColor: "#582c83" }} />
                    <TableCell sx={{ borderColor: "#582c83" }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseOrder.partNumbers && purchaseOrder.partNumbers!
                    .slice()
                    .sort((a, b) => (a.lineNumber || 0) - (b.lineNumber || 0))
                    .map((row, index) => (
                      <PurchaseOrderPartNumberRow
                        key={index + "POPartNumber" + row.mpn}
                        purchaseOrderNumber={purchaseOrder.purchaseOrderNumber || ""}
                        partNumber={row}
                      />
                    ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </Card >
      </>
    );
};

export default inject("store")(observer(PurchaseOrderView));
